import { BOOK_MEETING, LEADS_TABLE } from "codes";
import type {
  BookMeetingCallTypeOption,
  BookMeetingUrlParams,
} from "types/bookMeeting";
import {
  BASE_CALENDLY_CONFIG,
  BOOK_MEETING_ACCOUNT_ID_PATTERN,
  BOOK_MEETING_MANAGERS,
  BOOK_MEETING_TAB_TO_CALL_TYPE,
  BookMeetingCallType,
  BookMeetingCallTypeLabel,
  BookMeetingQueryKey,
  UsersTableHighlightQueryKey,
  COMPANY_SUBJECT_CALL_TYPES,
  RM_FALLBACK_EMAIL,
  RM_MEETING_DESCRIPTION,
  type BookMeetingManagerKey,
  type BookMeetingTabParamValue,
} from "./bookMeetingConstants";

export const buildBookMeetingUrl = ({
  tab,
  accountId,
}: BookMeetingUrlParams): string => {
  const params = new URLSearchParams();
  if (tab) {
    params.set(BookMeetingQueryKey.TAB, tab);
  }
  if (accountId) {
    params.set(BookMeetingQueryKey.ACCOUNT_ID, accountId);
  }
  const query = params.toString();
  return query ? `${BOOK_MEETING}?${query}` : BOOK_MEETING;
};

export const canSendBookMeetingPriorityNotification = (
  accountId?: string | null,
): boolean => BOOK_MEETING_ACCOUNT_ID_PATTERN.test((accountId ?? "").trim());

export const buildUserFullName = (
  user?: { first_name?: string | null; last_name?: string | null } | null,
): string => `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim();

export type BookMeetingPriorityNotificationPayload = {
  opportunity_id: string;
  opportunity_name: string;
  client_name?: string;
  internal_name?: string;
  contact_id?: string;
  priority: boolean;
};

export const buildBookMeetingPriorityNotificationPayload = ({
  bookingAccountId,
  company,
  currentUser,
  teamTitle,
}: {
  bookingAccountId: string;
  company?: { name?: string; internal_name?: string };
  currentUser?: {
    id?: string;
    first_name?: string | null;
    last_name?: string | null;
  };
  teamTitle: string;
}):
  | { canNotify: true; payload: BookMeetingPriorityNotificationPayload }
  | { canNotify: false; payload: null } => {
  // teamTitle is the selected department card (Support, Risk, Operations, Sales, RM).
  const opportunityName = `Meeting scheduled with ${teamTitle}`;

  if (canSendBookMeetingPriorityNotification(bookingAccountId)) {
    return {
      canNotify: true,
      payload: {
        opportunity_id: bookingAccountId,
        opportunity_name: opportunityName,
        client_name: company?.name,
        internal_name: company?.internal_name,
        priority: true,
      },
    };
  }

  const userFullName = buildUserFullName(currentUser);
  const contactId = currentUser?.id?.trim();
  if (userFullName && contactId) {
    return {
      canNotify: true,
      payload: {
        opportunity_id: contactId,
        contact_id: contactId,
        opportunity_name: opportunityName,
        client_name: userFullName,
        priority: true,
      },
    };
  }

  return { canNotify: false, payload: null };
};

export const buildUsersTableHighlightUrl = (contactId: string): string => {
  const params = new URLSearchParams({
    [UsersTableHighlightQueryKey.CONTACT_ID]: contactId,
  });
  return `${LEADS_TABLE}?${params.toString()}`;
};

export const getCallTypeFromTabParam = (
  tabParam: string | null | undefined,
): string | undefined => {
  if (!tabParam) {
    return undefined;
  }
  return BOOK_MEETING_TAB_TO_CALL_TYPE[tabParam as BookMeetingTabParamValue];
};

export const resolveCalendlyInviteeName = ({
  callType,
  companyName,
  userFullName,
}: {
  callType: string;
  companyName: string;
  userFullName: string;
}): string =>
  // Company name alone — Calendly/Google already append host "Incomlend" in the subject.
  COMPANY_SUBJECT_CALL_TYPES.has(callType) && companyName
    ? companyName
    : userFullName;

export const toManagerCallTypeOption = (
  callType: BookMeetingManagerKey,
): BookMeetingCallTypeOption => {
  const manager = BOOK_MEETING_MANAGERS[callType];
  return {
    value: callType,
    label: BookMeetingCallTypeLabel[callType],
    description: manager.description,
    calendlyUrl: manager.calendlyUrl,
    email: manager.email,
  };
};

export const buildRmCallTypeOption = ({
  rmName,
  calendlyUrl,
}: {
  rmName: string;
  calendlyUrl: string;
}): BookMeetingCallTypeOption => ({
  value: BookMeetingCallType.RM,
  label: `Relationship Manager (${rmName})`,
  description: RM_MEETING_DESCRIPTION,
  calendlyUrl,
  email: RM_FALLBACK_EMAIL,
});

export const getManagerConfig = (callType: string) =>
  BOOK_MEETING_MANAGERS[callType as BookMeetingManagerKey] ??
  BOOK_MEETING_MANAGERS[BookMeetingCallType.SUPPORT];

export const buildRmManagerConfig = ({
  rmName,
  calendlyUrl,
}: {
  rmName: string;
  calendlyUrl: string;
}) => ({
  title: `Relationship Manager (${rmName})`,
  email: RM_FALLBACK_EMAIL,
  calendlyUrl,
  description: RM_MEETING_DESCRIPTION,
});

export { BASE_CALENDLY_CONFIG };
